import {
    computed,
    effect,
    inject,
    Injectable,
    signal,
    untracked,
} from '@angular/core';
import { Subject, map, retry, takeUntil } from 'rxjs';
import * as turf from '@turf/turf';
import { isEqual } from 'lodash';
import { VehicleStore } from 'src/app/modules/core/store';
import { AppStateService } from 'src/app/modules/core/services/app-state.service';
import { MapApiService } from 'src/app/modules/core/services/map-api/map-api.service';
import { VehicleInfoService } from 'src/app/modules/core/services/vehicle-info.service';
import {
    CompactListItem,
    Vehicle,
    VehicleBlockActivityInfo,
} from 'src/app/modules/shared/models';
import { VehiclePunctualityConfig } from 'src/app/modules/shared/models/vehicle/vehicle-punctuality-config';
import { VEHICLE_COLOR_RGB } from 'src/app/modules/shared/models/vehicle/vehicle-icons-enum';
import { LineSearchResult } from 'src/app/components/traffic-deviations/components/searches/components/search-stop/interfaces/line-search-result';
import {
    LineViewStop,
    LineViewVariant,
    LineViewVehicle,
} from './line-view.models';

@Injectable({ providedIn: 'root' })
export class LineViewService {
    // ─── Feature toggles ──────────────────────────────────────────────────────

    /**
     * When true, segment rows between stops are sized proportionally to the
     * real-world distance between them (short / medium / large tiers).
     * Flip via `localStorage.setItem('lineView.variableSpacing', 'false')` to
     * revert to the legacy uniform 18 px rows for a designer A/B comparison.
     */
    readonly variableSpacingEnabled =
        localStorage.getItem('lineView.variableSpacing') !== 'false';

    /**
     * When true, vehicles are shown between stops after a Departure event and
     * their position is interpolated from GPS coordinates.
     * Flip via `localStorage.setItem('lineView.continuousMotion', 'false')` to
     * revert to stop-only snapping (legacy behaviour).
     */
    readonly continuousMotionEnabled =
        localStorage.getItem('lineView.continuousMotion') !== 'false';
    // ─── Public signals ───────────────────────────────────────────────────────

    /** Fully mapped vehicles on the currently active line. */
    readonly vehicles = signal<LineViewVehicle[]>([]);

    /** True while stop/variant data is being fetched from the API. */
    readonly loadingVariants = signal(false);

    /** Stops for the currently active line, from cache when available. */
    readonly activeStops = computed<LineViewStop[]>(
        () => this._stopsCache().get(this._activeLineId()) ?? [],
    );

    /** Variants for the currently active line, from cache when available. */
    readonly activeVariants = computed<LineViewVariant[]>(
        () => this._variantsCache().get(this._activeLineId()) ?? [],
    );

    /** True if the service has been activated for the given lineId. */
    isActiveForLine(lineId: string): boolean {
        return this._activeLineId() === lineId;
    }

    /** True if any currently displayed vehicle had its activity fetch exhausted. */
    readonly hasVehiclesWithFailedActivities = computed(() =>
        this.vehicles().some((v) => v.activitiesFailed),
    );

    // ─── Private state ────────────────────────────────────────────────────────

    private readonly _activeLineId = signal('');
    private readonly _rawVehicles = signal<Vehicle[]>([]);
    private readonly _blockActivitiesMap = signal<
        Map<string, VehicleBlockActivityInfo[]>
    >(new Map());
    private readonly _pendingActivities = new Set<string>();
    private readonly _failedActivities = signal<Set<string>>(new Set());
    private readonly _activityRetries = new Map<string, number>();
    private readonly _stopsCache = signal<Map<string, LineViewStop[]>>(
        new Map(),
    );
    private readonly _variantsCache = signal<Map<string, LineViewVariant[]>>(
        new Map(),
    );
    private readonly _loadingLineIds = new Set<string>();
    private readonly _cacheExpiryMap = new Map<string, number>();
    /** Last GPS-derived fractional position per vehicle+segment.
     *  When a vehicle update has no GPS reading we re-emit the cached value so
     *  the vehicle freezes in place instead of advancing on a time estimate. */
    private readonly _lastFractionMap = new Map<string, number>();

    private readonly _vehicleStore = inject(VehicleStore);
    private readonly _vehicleInfoService = inject(VehicleInfoService);
    private readonly _mapApiService = inject(MapApiService);
    private readonly _appState = inject(AppStateService);

    private _vehicleSub$ = new Subject<void>();
    private _activitySub$ = new Subject<void>();

    constructor() {
        // Re-map vehicles whenever raw vehicles, activities, stops, or variants change.
        // Only emit vehicles whose activity data is already resolved (loaded or failed),
        // so the UI never renders a vehicle with an incorrect initial direction.
        effect(() => {
            const raw = this._rawVehicles();
            const activitiesMap = this._blockActivitiesMap();
            const failedSet = this._failedActivities();
            const stops = this.activeStops();
            const variants = this.activeVariants();
            const mapped = raw
                .filter(
                    (v) =>
                        activitiesMap.has(v.inventoryNumber) ||
                        failedSet.has(v.inventoryNumber),
                )
                .map((v) => this._mapVehicle(v, stops, variants));
            if (
                !isEqual(
                    mapped,
                    untracked(() => this.vehicles()),
                )
            ) {
                this.vehicles.set(mapped);
            }
        });
    }

    // ─── Public API ───────────────────────────────────────────────────────────

    /**
     * Switch the service to a specific line. Starts the vehicle stream and,
     * if the stop/variant data is not yet cached, fetches it from the API.
     * Safe to call multiple times with the same lineId — will not re-fetch cached data.
     */
    activateForLine(
        lineId: string,
        routeNumber: string,
        garageCode: string[],
    ): Promise<void> {
        const isSameLine = this._activeLineId() === lineId;
        this._activeLineId.set(lineId);

        if (!isSameLine) {
            this._blockActivitiesMap.set(new Map());
            this._pendingActivities.clear();
            this._failedActivities.set(new Set());
            this._activityRetries.clear();
            this._lastFractionMap.clear();
            this.vehicles.set([]);
            this._activitySub$.next();
            this._activitySub$.complete();
            this._activitySub$ = new Subject<void>();
        }

        this._startVehicleSubscription(lineId);

        const isCacheValid =
            this._stopsCache().has(lineId) &&
            (this._cacheExpiryMap.get(lineId) ?? 0) > Date.now();
        if (!isCacheValid && !this._loadingLineIds.has(lineId)) {
            return this._loadLineViewData(lineId, routeNumber, garageCode);
        }
        return Promise.resolve();
    }

    /**
     * Stops the vehicle stream.
     * Cached stops/variants survive so re-opening the blade is instant.
     * The caller is responsible for clearing the inventory filter when fully leaving the line.
     */
    deactivate(): void {
        this._pendingActivities.clear();
        this._failedActivities.set(new Set());
        this._activityRetries.clear();
        this._lastFractionMap.clear();
        this._activitySub$.next();
        this._activitySub$.complete();
        this._activitySub$ = new Subject<void>();
        this._vehicleSub$.next();
        this._vehicleSub$.complete();
        this._vehicleSub$ = new Subject<void>();
    }

    /**
     * Fires getVehicleActivities for each vehicle and merges results into
     * the internal activities map. Call whenever the vehicle list or active tab changes.
     */
    refreshActivities(items: CompactListItem[]): void {
        if (!items?.length) return;

        const alreadyLoaded = this._blockActivitiesMap();
        items.forEach((item) => {
            const vehicle = item.object as Vehicle;
            if (!vehicle?.inventoryNumber) return;
            if (alreadyLoaded.has(vehicle.inventoryNumber)) return;
            if (this._pendingActivities.has(vehicle.inventoryNumber)) return;
            if (this._failedActivities().has(vehicle.inventoryNumber)) return;

            this._pendingActivities.add(vehicle.inventoryNumber);
            this._vehicleInfoService
                .getVehicleActivities(
                    vehicle.inventoryNumber,
                    vehicle.trafficDate,
                )
                .pipe(retry(2), takeUntil(this._activitySub$))
                .subscribe({
                    next: (block) => {
                        this._pendingActivities.delete(vehicle.inventoryNumber);
                        this._activityRetries.delete(vehicle.inventoryNumber);
                        if (block?.vehicleActivities?.length) {
                            this._blockActivitiesMap.update((current) => {
                                const updated = new Map(current);
                                updated.set(
                                    vehicle.inventoryNumber,
                                    block.vehicleActivities,
                                );
                                return updated;
                            });
                        }
                    },
                    error: () => {
                        this._pendingActivities.delete(vehicle.inventoryNumber);
                        this._activityRetries.delete(vehicle.inventoryNumber);
                        this._failedActivities.update((current) => {
                            const updated = new Set(current);
                            updated.add(vehicle.inventoryNumber);
                            return updated;
                        });
                    },
                });
        });
    }

    // ─── Private helpers ──────────────────────────────────────────────────────

    private _endOfDayTimestamp(): number {
        const d = new Date();
        return new Date(
            d.getFullYear(),
            d.getMonth(),
            d.getDate(),
            23,
            59,
            59,
            999,
        ).getTime();
    }

    private _startVehicleSubscription(lineId: string): void {
        this._vehicleSub$.next();
        this._vehicleSub$.complete();
        this._vehicleSub$ = new Subject<void>();

        // Vehicle objects in the store are mutated in-place (Object.assign), so a deep
        // isEqual comparison against the previous emission always passes even when position
        // changes. We drop distinctUntilChanged here and rely on the isEqual guard inside
        // the effect (which compares freshly-mapped LineViewVehicle objects) to prevent
        // unnecessary vehicles.set() calls.
        this._vehicleStore
            .getVehiclesState()
            .pipe(
                map((vehicles) =>
                    (vehicles ?? []).filter((v) => v?.lineId === lineId),
                ),
                takeUntil(this._vehicleSub$),
            )
            .subscribe((rawVehicles) => {
                this._rawVehicles.set(rawVehicles);
            });
    }

    private async _loadLineViewData(
        lineId: string,
        routeNumber: string,
        garageCode: string[],
    ): Promise<void> {
        if (!routeNumber || !garageCode?.length) return;

        this._loadingLineIds.add(lineId);
        this.loadingVariants.set(true);

        try {
            const garage = this._appState.stateSnapshot.garages?.find(
                (g) => g.code === garageCode[0],
            );
            const hastusRegion = garage?.hastusRegionCode;
            if (!hastusRegion) return;

            const now = new Date();
            const startDateTime = new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate(),
                0,
                0,
                0,
            ).toISOString();
            const endDateTime = new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate(),
                23,
                59,
                59,
            ).toISOString();

            const routes = await this._mapApiService.postRoutesByLine(
                routeNumber,
                [hastusRegion],
                startDateTime,
                endDateTime,
            );

            if (routes?.length > 0) {
                await this._buildAndCacheStopsAndVariants(
                    lineId,
                    routes,
                    hastusRegion,
                    startDateTime,
                    endDateTime,
                );
            }
        } catch (error) {
            console.error(
                'LineViewService: failed to load line view data',
                error,
            );
        } finally {
            this._loadingLineIds.delete(lineId);
            this.loadingVariants.set(false);
        }
    }

    private async _buildAndCacheStopsAndVariants(
        lineId: string,
        routes: LineSearchResult[],
        hastusRegion: string,
        startDateTime: string,
        endDateTime: string,
    ): Promise<void> {
        const allRouteStops = await Promise.all(
            routes.map((route) =>
                this._mapApiService.getStopsByRouteId(
                    route.routeId,
                    hastusRegion,
                    startDateTime,
                    endDateTime,
                    route.direction,
                    route.templateTripId,
                ),
            ),
        );

        // Build master stop list — longest route is the canonical order, then sequence-merge
        // each other variant so missing stops land next to their neighbors instead of at the end.
        const longestIndex = allRouteStops.reduce(
            (best, stops, i) =>
                (stops?.length || 0) > (allRouteStops[best]?.length || 0)
                    ? i
                    : best,
            0,
        );
        const masterStopNames: string[] = (
            allRouteStops[longestIndex] || []
        ).map((s) => s.stopAreaDescription);

        for (let r = 0; r < allRouteStops.length; r++) {
            if (r === longestIndex) continue;
            const routeNames = (allRouteStops[r] || []).map(
                (s) => s.stopAreaDescription,
            );

            // Detect direction: if known indices in master are mostly decreasing,
            // this variant runs the line in reverse — reverse it before merging.
            const knownIndices = routeNames
                .map((n) => masterStopNames.indexOf(n))
                .filter((i) => i >= 0);
            let increases = 0,
                decreases = 0;
            for (let k = 1; k < knownIndices.length; k++) {
                if (knownIndices[k] > knownIndices[k - 1]) increases++;
                else if (knownIndices[k] < knownIndices[k - 1]) decreases++;
            }
            const orderedNames =
                decreases > increases ? [...routeNames].reverse() : routeNames;

            // Sequence-merge: insert missing stops right after the most recently
            // seen master stop, preserving the variant's "X comes between A and B" order.
            let insertAfter = -1;
            for (const name of orderedNames) {
                const idx = masterStopNames.indexOf(name);
                if (idx >= 0) {
                    insertAfter = idx;
                } else {
                    insertAfter += 1;
                    masterStopNames.splice(insertAfter, 0, name);
                }
            }
        }

        // Collect representative coordinates per stop name (first occurrence wins;
        // the longest route is processed first so its coords take precedence).
        const coordMap = new Map<string, { lat: number; lon: number }>();
        const orderedIndices = [
            longestIndex,
            ...Array.from({ length: allRouteStops.length }, (_, i) => i).filter(
                (i) => i !== longestIndex,
            ),
        ];
        for (const idx of orderedIndices) {
            for (const stopArea of allRouteStops[idx] || []) {
                const key = stopArea.stopAreaDescription.toLowerCase();
                if (!coordMap.has(key) && stopArea.stopPlaces?.length > 0) {
                    const place = stopArea.stopPlaces[0];
                    if (place.placeLatitude && place.placeLongitude) {
                        coordMap.set(key, {
                            lat: place.placeLatitude,
                            lon: place.placeLongitude,
                        });
                    }
                }
            }
        }

        // Build stops with coordinates.
        const stops: LineViewStop[] = masterStopNames.map((name, index) => {
            const coord = coordMap.get(name.toLowerCase());
            return { name, index, lat: coord?.lat, lon: coord?.lon };
        });

        // Compute straight-line distance to the next stop for each stop (in km).
        for (let i = 0; i < stops.length - 1; i++) {
            const cur = stops[i];
            const nxt = stops[i + 1];
            if (
                cur.lat != null &&
                cur.lon != null &&
                nxt.lat != null &&
                nxt.lon != null
            ) {
                cur.distanceToNextKm = turf.distance(
                    turf.point([cur.lon, cur.lat]),
                    turf.point([nxt.lon, nxt.lat]),
                    { units: 'kilometers' },
                );
            }
        }

        // Absolute km thresholds — same scale on every line so users learn the visual mapping.
        for (let i = 0; i < stops.length - 1; i++) {
            const d = stops[i].distanceToNextKm;
            // Appended stops without coords fall back to 'medium' so they remain visible.
            stops[i].spacingClass =
                d == null
                    ? 'medium'
                    : d < 0.5
                      ? 'short'
                      : d < 2.0
                        ? 'medium'
                        : d < 5.0
                          ? 'large'
                          : 'extra-large';
        }

        // Build per-route variants.
        const variantColors = [
            '#333333',
            '#0078D7',
            '#E74C3C',
            '#F39C12',
            '#2ECC71',
            '#9B59B6',
            '#7CB342',
            '#00897B',
            '#E91E63',
            '#795548',
        ];
        const builtVariants: LineViewVariant[] = routes.map((route, index) => {
            const stopNames = (allRouteStops[index] || []).map(
                (s) => s.stopAreaDescription,
            );
            const indices = stopNames
                .map((name) => masterStopNames.indexOf(name))
                .filter((i) => i >= 0);
            return {
                routeId: route.routeId,
                color: '',
                fromStopIndex: indices.length > 0 ? Math.min(...indices) : 0,
                toStopIndex:
                    indices.length > 0
                        ? Math.max(...indices)
                        : stops.length - 1,
                stopNames,
                direction: route.direction,
            };
        });

        const merged = this._mergeBidirectionalVariants(builtVariants);

        // Sort by span (most stops between start and end in the master list) descending.
        // Tiebreak by direction: upwards (1) first, merged (undefined) second, downwards (2) last.
        const directionOrder = (d: number | undefined): number =>
            d === 1 ? 0 : d === undefined ? 2 : 2;

        merged.sort((a, b) => {
            const spanDiff =
                b.toStopIndex -
                b.fromStopIndex -
                (a.toStopIndex - a.fromStopIndex);
            if (spanDiff !== 0) return spanDiff;
            return directionOrder(a.direction) - directionOrder(b.direction);
        });

        // Assign colors based on sorted position so the longest-span variant always gets the first color.
        merged.forEach((v, i) => {
            v.color = variantColors[i % variantColors.length];
        });

        this._stopsCache.update((m) => {
            const n = new Map(m);
            n.set(lineId, stops);
            return n;
        });
        this._variantsCache.update((m) => {
            const n = new Map(m);
            n.set(lineId, merged);
            return n;
        });
        this._cacheExpiryMap.set(lineId, this._endOfDayTimestamp());
    }

    private _mergeBidirectionalVariants(
        builtVariants: LineViewVariant[],
    ): LineViewVariant[] {
        const used = new Set<number>();
        const merged: LineViewVariant[] = [];

        for (let i = 0; i < builtVariants.length; i++) {
            if (used.has(i)) continue;
            const a = builtVariants[i];
            const partnerIdx = this._findBidirectionalPartnerIndex(
                i,
                builtVariants,
                used,
            );

            if (partnerIdx >= 0) {
                const b = builtVariants[partnerIdx];
                const outbound =
                    (a.direction ?? Infinity) <= (b.direction ?? Infinity)
                        ? a
                        : b;
                merged.push({ ...outbound, direction: undefined });
                used.add(i);
                used.add(partnerIdx);
            } else {
                merged.push(a);
                used.add(i);
            }
        }
        return merged;
    }

    private _findBidirectionalPartnerIndex(
        i: number,
        variants: LineViewVariant[],
        used: Set<number>,
    ): number {
        const aKey = this._variantEndpointKey(variants[i]);
        for (let j = i + 1; j < variants.length; j++) {
            if (
                !used.has(j) &&
                this._variantEndpointKey(variants[j]) === aKey
            ) {
                return j;
            }
        }
        return -1;
    }

    private _variantEndpointKey(variant: LineViewVariant): string {
        if (variant.stopNames.length === 0) return '';
        return [...variant.stopNames]
            .map((s) => s.toLowerCase().trim())
            .sort((a, b) => a.localeCompare(b))
            .join('\x00');
    }

    // ─── Pure mapping (moved from LineViewComponent) ──────────────────────────

    private _mapVehicle(
        vehicle: Vehicle,
        stops: LineViewStop[],
        variantList: LineViewVariant[],
    ): LineViewVehicle {
        const config: VehiclePunctualityConfig =
            this._appState.stateSnapshot.punctualityConfig;
        const cfg = config.getConfig(
            vehicle.departureDeviation?.deviation?.getTotalSeconds(),
            vehicle.arrivalDeviation?.deviation?.getTotalSeconds(),
            vehicle.onTomLayOvr,
        );

        // Prefer the deviation that matches progressType; the other deviation can be stale
        // on the vehicle object because VehicleStore.update only removes it from the
        // *incoming* message before Object.assign — the existing vehicle retains the old value.
        const isArrival = vehicle.progressType === 'Arrival';
        const stopName = isArrival
            ? (vehicle.arrivalDeviation?.stopName ??
              vehicle.departureDeviation?.stopName)
            : (vehicle.departureDeviation?.stopName ??
              vehicle.arrivalDeviation?.stopName);
        let matchIndex = -1;
        let stopPosition = 0;
        let atStop = true;

        if (stopName && stops.length > 0) {
            matchIndex = stops.findIndex(
                (s) => s.name.toLowerCase() === stopName.toLowerCase(),
            );
            if (matchIndex >= 0) {
                stopPosition = Math.min(matchIndex, stops.length - 1);
                atStop =
                    vehicle.progressType === 'Departure' ||
                    vehicle.progressType === 'Arrival';
            }
        }

        const allActivities =
            this._blockActivitiesMap().get(vehicle.inventoryNumber) ?? [];
        const { currentActivity, nextActivity } =
            this._findCurrentAndNextActivity(vehicle, allActivities);

        const activityDirection = currentActivity?.tripDescription
            ? (this._parseTripEndpoints(currentActivity.tripDescription, stops)
                  ?.goingDown ?? null)
            : null;

        const nextStopName = isArrival
            ? (vehicle.arrivalDeviation?.nextStopName ??
              vehicle.departureDeviation?.nextStopName)
            : (vehicle.departureDeviation?.nextStopName ??
              vehicle.arrivalDeviation?.nextStopName);
        const nextIdx = nextStopName
            ? stops.findIndex(
                  (s) => s.name.toLowerCase() === nextStopName.toLowerCase(),
              )
            : -1;
        const nextStopDir: boolean | null =
            nextIdx >= 0 ? nextIdx > stopPosition : null;
        const knownDirection: boolean | null = nextStopDir ?? activityDirection;

        const seqNum = isArrival
            ? (vehicle.arrivalDeviation?.sequenceNumber ??
              vehicle.departureDeviation?.sequenceNumber ??
              0)
            : (vehicle.departureDeviation?.sequenceNumber ??
              vehicle.arrivalDeviation?.sequenceNumber ??
              0);
        const tripEndpoints = currentActivity?.tripDescription
            ? this._parseTripEndpoints(currentActivity.tripDescription, stops)
            : null;

        const resolved = this._resolveVariant(
            variantList,
            stopName,
            stopPosition,
            seqNum,
            tripEndpoints,
            knownDirection,
            currentActivity?.tripDescription,
        );
        const { matchedVariant } = resolved;
        let { goingDown } = resolved;

        // ── Continuous motion: fractional stopPosition for vehicles between stops ─
        // Applied after goingDown is resolved so dirSign is correct.
        // dbg* values are hoisted so the per-vehicle debug log below can read them.
        let dbgFromStop: LineViewStop | undefined;
        let dbgToStop: LineViewStop | undefined;
        let dbgFrac: number | undefined;
        let dbgTargetIdx: number | undefined;
        if (
            this.continuousMotionEnabled &&
            vehicle.progressType === 'Departure' &&
            matchIndex >= 0
        ) {
            atStop = false;
            const dirSign = goingDown ? 1 : -1;
            const targetIdx = nextIdx >= 0 ? nextIdx : matchIndex + dirSign;
            dbgTargetIdx = targetIdx;
            if (targetIdx >= 0 && targetIdx < stops.length) {
                const fromStop = stops[matchIndex];
                const toStop = stops[targetIdx];
                const frac = this._projectFraction(vehicle, fromStop, toStop);
                stopPosition = matchIndex + (targetIdx - matchIndex) * frac;
                // Sync goingDown to the ACTUAL movement direction so the pin and
                // left/right placement stay correct when nextIdx disagrees with the
                // variant-resolved direction (e.g. trip description says "outbound"
                // but the deviation says the next stop is in the opposite direction).
                goingDown = targetIdx > matchIndex;
                dbgFromStop = fromStop;
                dbgToStop = toStop;
                dbgFrac = frac;
            }
        }

        const variantIndex = matchedVariant
            ? variantList.indexOf(matchedVariant)
            : 0;
        const deviationSecs =
            vehicle.departureDeviation?.deviation?.getTotalSeconds();
        const deviationMinutes =
            deviationSecs == null
                ? undefined
                : Math.round(Math.abs(deviationSecs) / 60);

        // Per-vehicle line-view placement log.
        // Enable for one vehicle:  localStorage.setItem('debug.mapVehicleLog', '5449')
        // Or many (comma-sep):     localStorage.setItem('debug.mapVehicleLog', '5449,5448')
        // Disable:                 localStorage.removeItem('debug.mapVehicleLog')
        const filter = localStorage.getItem('debug.mapVehicleLog') ?? '';
        if (filter && filter.split(',').map(s => s.trim()).includes(
            vehicle.inventoryNumberWithoutPrefix,
        )) {
            const dep = vehicle.departureDeviation;
            const arr = vehicle.arrivalDeviation;

            // Resolve segment description + projection telemetry
            let segment: string;
            let fractionPct: number | null = null;
            let distFromFromKm: number | null = null;
            let distFromToKm: number | null = null;
            let fractionSource: 'gps' | 'cache' | 'default-mid' | 'n/a' = 'n/a';
            let cachedFraction: number | undefined;

            if (dbgFromStop && dbgToStop) {
                segment = `${dbgFromStop.name} → ${dbgToStop.name}`;
                fractionPct = Math.round((dbgFrac ?? 0.5) * 100);
                const cacheKey = `${vehicle.inventoryNumber}|${dbgFromStop.index}|${dbgToStop.index}`;
                cachedFraction = this._lastFractionMap.get(cacheKey);
                const hasGps =
                    vehicle.position?.latitude != null &&
                    vehicle.position?.longitude != null;
                const hasCoords =
                    dbgFromStop.lat != null &&
                    dbgFromStop.lon != null &&
                    dbgToStop.lat != null &&
                    dbgToStop.lon != null;
                if (hasGps && hasCoords) {
                    const vPt = turf.point([
                        vehicle.position.longitude,
                        vehicle.position.latitude,
                    ]);
                    distFromFromKm = +turf
                        .distance(turf.point([dbgFromStop.lon!, dbgFromStop.lat!]), vPt)
                        .toFixed(3);
                    distFromToKm = +turf
                        .distance(turf.point([dbgToStop.lon!, dbgToStop.lat!]), vPt)
                        .toFixed(3);
                    fractionSource = 'gps';
                } else if (cachedFraction !== undefined) {
                    fractionSource = 'cache';
                } else {
                    fractionSource = 'default-mid';
                }
            } else if (matchIndex >= 0 && stops[matchIndex]) {
                segment = atStop
                    ? `at ${stops[matchIndex].name} (idx ${matchIndex})`
                    : `${stops[matchIndex].name} (idx ${matchIndex}, no segment projection)`;
            } else {
                segment = `unknown — chosenStop="${stopName ?? ''}", matchIndex=${matchIndex}`;
            }

            const summary = atStop
                ? `[lineView ${vehicle.inventoryNumberWithoutPrefix}] ${segment} — atStop`
                : `[lineView ${vehicle.inventoryNumberWithoutPrefix}] ${segment} @ ${fractionPct ?? '?'}% (dir=${goingDown ? 'down' : 'up'})`;

            console.log(summary, {
                cause: {
                    progressType: vehicle.progressType,
                    chosenStop: stopName,
                    nextStopName,
                    matchIndex,
                    nextIdx,
                    targetIdx: dbgTargetIdx,
                    tripDescription: currentActivity?.tripDescription,
                    continuousMotionEnabled: this.continuousMotionEnabled,
                },
                projection: {
                    stopPosition: +stopPosition.toFixed(3),
                    fractionPct,
                    fractionSource,
                    distFromFromKm,
                    distFromToKm,
                    cachedFraction,
                    atStop,
                    goingDown,
                },
                dep: dep && {
                    stopName: dep.stopName,
                    nextStopName: dep.nextStopName,
                    seq: dep.sequenceNumber,
                    measuredAt: dep.measuredAt,
                },
                arr: arr && {
                    stopName: arr.stopName,
                    nextStopName: arr.nextStopName,
                    seq: arr.sequenceNumber,
                    measuredAt: arr.measuredAt,
                },
                variant: matchedVariant && {
                    idx: variantIndex,
                    color: matchedVariant.color,
                    from: matchedVariant.stopNames[0],
                    to: matchedVariant.stopNames.at(-1),
                    direction: matchedVariant.direction,
                },
                trip: {
                    number: vehicle.tripNumber,
                    route: vehicle.routeNumber,
                },
                gps: vehicle.position && {
                    lat: vehicle.position.latitude,
                    lon: vehicle.position.longitude,
                    ts: vehicle.position.timestamp,
                },
            });
        }

        return {
            inventoryNumber: vehicle.inventoryNumber,
            inventoryNumberWithoutPrefix: vehicle.inventoryNumberWithoutPrefix,
            color: cfg.color || VEHICLE_COLOR_RGB.Gray,
            hasWarning: vehicle.displayableWarnings?.length > 0,
            warnings: vehicle.displayableWarnings ?? [],
            stopPosition,
            atStop,
            goingDown,
            variantIndex,
            variantColor:
                matchedVariant?.color || variantList[0]?.color || '#333333',
            deviationText: vehicle.departureDeviation?.text,
            deviationMinutes,
            currentActivity,
            nextActivity,
            activitiesFailed: this._failedActivities().has(
                vehicle.inventoryNumber,
            ),
        };
    }

    /**
     * Computes a fractional position [0.05, 0.95] representing how far along the
     * segment from `fromStop` to `toStop` the vehicle is, using a GPS-based
     * straight-line projection. When no GPS reading is available on this tick,
     * returns the last fraction computed for the same vehicle+segment so the
     * vehicle freezes in place rather than advancing on a time estimate.
     * Defaults to 0.5 (segment midpoint) when no GPS has ever been seen for
     * the segment.
     */
    private _projectFraction(
        vehicle: Vehicle,
        fromStop: LineViewStop,
        toStop: LineViewStop,
    ): number {
        const hasGps =
            vehicle.position?.latitude != null &&
            vehicle.position?.longitude != null;
        const hasCoords =
            fromStop.lat != null &&
            fromStop.lon != null &&
            toStop.lat != null &&
            toStop.lon != null;
        const cacheKey = `${vehicle.inventoryNumber}|${fromStop.index}|${toStop.index}`;

        if (hasGps && hasCoords) {
            // Distance-based projection: compare GPS distance from the vehicle to each
            // stop endpoint.  This is more robust than a dot-product on routes with
            // significant curvature, because a vehicle closer to toStop will always
            // have a smaller distFromTo regardless of how the road bends.
            const vPt = turf.point([
                vehicle.position.longitude,
                vehicle.position.latitude,
            ]);
            const fromPt = turf.point([fromStop.lon!, fromStop.lat!]);
            const toPt = turf.point([toStop.lon!, toStop.lat!]);

            const distFromFrom = turf.distance(fromPt, vPt); // km, vehicle → fromStop
            const distFromTo = turf.distance(toPt, vPt); // km, vehicle → toStop
            const total = distFromFrom + distFromTo;
            if (total > 0) {
                const t = Math.min(Math.max(distFromFrom / total, 0.05), 0.95);
                this._lastFractionMap.set(cacheKey, t);
                return t;
            }
        }

        return this._lastFractionMap.get(cacheKey) ?? 0.5;
    }

    // eslint-disable-next-line max-params
    private _debug2089Before(
        vehicle: Vehicle,
        allActivities: VehicleBlockActivityInfo[],
        currentActivity: VehicleBlockActivityInfo | undefined,
        stopName: string | undefined,
        stopPosition: number,
        seqNum: number,
        nextStopName: string | undefined,
        nextIdx: number,
        activityDirection: boolean | null,
        nextStopDir: boolean | null,
        knownDirection: boolean | null,
        tripEndpoints: { from: string; to: string; goingDown: boolean } | null,
        stops: LineViewStop[],
        variantList: LineViewVariant[],
    ): void {
        console.group(
            `[LineView DBG] Vehicle ${vehicle.inventoryNumber} / ${vehicle.inventoryNumberWithoutPrefix}`,
        );
        console.log(
            'vehicle.tripNumber:',
            vehicle.tripNumber,
            '| typeof:',
            typeof vehicle.tripNumber,
        );
        console.log(
            'vehicle.routeNumber:',
            vehicle.routeNumber,
            '| typeof:',
            typeof vehicle.routeNumber,
        );
        console.log('allActivities count:', allActivities.length);
        if (allActivities.length > 0) {
            console.table(
                allActivities.map((a) => ({
                    tripNumber: a.tripNumber,
                    tripNumber_typeof: typeof a.tripNumber,
                    routeName: a.routeName,
                    routePublicName: a.routePublicName,
                    tripDescription: a.tripDescription,
                    startTime: a.startTime,
                    endTime: a.endTime,
                })),
            );
        }
        console.log(
            'currentActivity:',
            currentActivity
                ? {
                      tripNumber: currentActivity.tripNumber,
                      routeName: currentActivity.routeName,
                      routePublicName: currentActivity.routePublicName,
                      tripDescription: currentActivity.tripDescription,
                  }
                : null,
        );
        console.log(
            'stopName:',
            stopName,
            '| stopPosition:',
            stopPosition,
            '| seqNum:',
            seqNum,
        );
        console.log('nextStopName:', nextStopName, '| nextIdx:', nextIdx);
        console.log(
            'activityDir:',
            activityDirection,
            '| nextStopDir:',
            nextStopDir,
            '| knownDirection:',
            knownDirection,
        );
        console.log('tripEndpoints (master stops):', tripEndpoints);
        console.log(
            'master stops count:',
            stops.length,
            '| first 5:',
            stops.slice(0, 5).map((s) => s.name),
        );
        console.log(
            'variants:',
            variantList.map((v, i) => ({
                index: i,
                color: v.color,
                stopNames_first: v.stopNames[0],
                stopNames_last: v.stopNames.at(-1),
                direction: v.direction,
            })),
        );
    }

    private _resolveVariant(
        variantList: LineViewVariant[],
        stopName: string | undefined,
        stopPosition: number,
        seqNum: number,
        tripEndpoints: { from: string; to: string; goingDown: boolean } | null,
        knownDirection: boolean | null,
        tripDescription?: string,
    ): { matchedVariant: LineViewVariant; goingDown: boolean } {
        const sn = stopName?.toLowerCase();
        const terminalDir = tripEndpoints?.goingDown ?? knownDirection;
        let matched: LineViewVariant | undefined;
        let dir: boolean | undefined;

        // 1+2. Direction-aware match (terminal then any-stop-in-list).
        if (terminalDir !== null && terminalDir !== undefined && sn) {
            ({ matched, dir } = this._matchByDirection(
                variantList,
                sn,
                terminalDir,
                tripEndpoints,
            ));
        }

        // 1.5. When master-stops parsing failed (tripEndpoints null) or direction is unknown,
        // match the raw trip description directly against each variant's actual endpoint names.
        if (!matched && tripDescription) {
            ({ matched, dir } = this._matchByTripDescriptionEndpoints(
                variantList,
                tripDescription,
                sn,
            ));
        }

        // 3. seqNum structural match.
        if (!matched && seqNum > 0 && sn) {
            const outMatch = variantList.find(
                (v) => v.stopNames[seqNum - 1]?.toLowerCase() === sn,
            );
            const inMatch = outMatch
                ? undefined
                : variantList.find(
                      (v) =>
                          v.direction === undefined &&
                          [...v.stopNames]
                              .reverse()
                              [seqNum - 1]?.toLowerCase() === sn,
                  );
            matched = outMatch ?? inMatch;
            if (matched) dir = !!outMatch;
        }

        // 4. Stop-name membership; infer direction from seqNum for merged bidirectional variants.
        if (!matched && sn) {
            ({ matched, dir } = this._matchByStopName(
                variantList,
                sn,
                seqNum,
                knownDirection,
                dir,
            ));
        }

        // 5. Index range fallback.
        matched ??= variantList.find(
            (v) =>
                stopPosition >= v.fromStopIndex &&
                stopPosition <= v.toStopIndex,
        );

        // 6. Last resort.
        matched ??= variantList[0];

        return {
            matchedVariant: matched,
            goingDown: dir ?? knownDirection ?? true,
        };
    }

    private _matchByDirection(
        variantList: LineViewVariant[],
        sn: string,
        terminalDir: boolean,
        tripEndpoints: { from: string; to: string; goingDown: boolean } | null,
    ): { matched: LineViewVariant | undefined; dir: boolean | undefined } {
        const getList = (v: LineViewVariant) =>
            terminalDir || v.direction !== undefined
                ? v.stopNames
                : [...v.stopNames].reverse();
        const fromLower = tripEndpoints?.from.toLowerCase();
        const toLower = tripEndpoints?.to.toLowerCase();
        const isTerminalMatch = (v: LineViewVariant) => {
            const list = getList(v);
            return (
                list.at(0)?.toLowerCase().trim() === fromLower &&
                list.at(-1)?.toLowerCase().trim() === toLower
            );
        };
        let matched: LineViewVariant | undefined = tripEndpoints
            ? variantList.find(
                  (v) =>
                      isTerminalMatch(v) &&
                      getList(v).some((s) => s.toLowerCase() === sn),
              )
            : undefined;

        // Partial endpoint match: both trip endpoints exist somewhere in the variant (short-working
        // trips that don't start at the variant's terminal). Prefer the shortest matching variant so
        // that a trip like "Skuru skola-Slussen" picks the shorter "Skurustugan-Slussen" variant
        // rather than the long "Nämdöstigen-Slussen" variant.
        if (!matched && fromLower && toLower) {
            const candidates = variantList.filter((v) => {
                const list = getList(v);
                return (
                    list.some((s) => s.toLowerCase().trim() === fromLower) &&
                    list.some((s) => s.toLowerCase().trim() === toLower)
                );
            });
            if (candidates.length > 0) {
                matched = candidates.reduce(
                    (best, v) =>
                        getList(v).length < getList(best).length ? v : best,
                    candidates[0],
                );
            }
        }

        matched ??= variantList.find((v) =>
            getList(v).some((s) => s.toLowerCase() === sn),
        );
        return { matched, dir: matched ? terminalDir : undefined };
    }

    private _matchByStopName(
        variantList: LineViewVariant[],
        sn: string,
        seqNum: number,
        knownDirection: boolean | null,
        dir: boolean | undefined,
    ): { matched: LineViewVariant | undefined; dir: boolean | undefined } {
        const candidate = variantList.find((v) =>
            v.stopNames.some((s) => s.toLowerCase() === sn),
        );
        if (!candidate) return { matched: undefined, dir };

        let resolvedDir = dir;
        if (candidate.direction === undefined && resolvedDir === undefined) {
            if (seqNum > 0) {
                const inOut =
                    candidate.stopNames[seqNum - 1]?.toLowerCase() === sn;
                const inIn =
                    [...candidate.stopNames]
                        .reverse()
                        [seqNum - 1]?.toLowerCase() === sn;
                if (inOut && !inIn) resolvedDir = true;
                else if (inIn && !inOut) resolvedDir = false;
            }
            resolvedDir ??= knownDirection ?? undefined;
        }
        return { matched: candidate, dir: resolvedDir };
    }

    /**
     * Matches a trip description (e.g. "Mölnvik-Slussen") directly against each
     * variant's actual first/last stop names, bypassing the master stops list.
     * Handles cases where the trip description stop names don't exactly match
     * the stop area descriptions stored in the master stops list.
     */
    private _matchByTripDescriptionEndpoints(
        variantList: LineViewVariant[],
        tripDescription: string,
        sn: string | undefined,
    ): { matched: LineViewVariant | undefined; dir: boolean | undefined } {
        const parts = this._splitTripDescriptionParts(tripDescription);
        for (const [fromPart, toPart] of parts) {
            const result = this._findVariantByEndpoints(
                variantList,
                fromPart,
                toPart,
                sn,
            );
            if (result.matched) return result;
        }
        return { matched: undefined, dir: undefined };
    }

    private _splitTripDescriptionParts(
        tripDescription: string,
    ): [string, string][] {
        const parts: [string, string][] = [];
        let idx = 0;
        while ((idx = tripDescription.indexOf('-', idx)) !== -1) {
            parts.push([
                tripDescription.substring(0, idx).trim().toLowerCase(),
                tripDescription
                    .substring(idx + 1)
                    .trim()
                    .toLowerCase(),
            ]);
            idx++;
        }
        return parts;
    }

    private _findVariantByEndpoints(
        variantList: LineViewVariant[],
        fromPart: string,
        toPart: string,
        sn: string | undefined,
    ): { matched: LineViewVariant | undefined; dir: boolean | undefined } {
        for (const v of variantList) {
            const fwd = this._checkEndpointMatch(
                v.stopNames,
                fromPart,
                toPart,
                sn,
            );
            if (fwd !== null) return { matched: v, dir: fwd };
            if (v.direction === undefined) {
                const inv = this._checkEndpointMatch(
                    [...v.stopNames].reverse(),
                    fromPart,
                    toPart,
                    sn,
                );
                if (inv !== null) return { matched: v, dir: !inv };
            }
        }
        return { matched: undefined, dir: undefined };
    }

    private _checkEndpointMatch(
        names: string[],
        fromPart: string,
        toPart: string,
        sn: string | undefined,
    ): boolean | null {
        const first = names[0]?.toLowerCase().trim();
        const last = names.at(-1)?.toLowerCase().trim();
        const hasSn = !sn || names.some((s) => s.toLowerCase() === sn);
        if (first === fromPart && last === toPart && hasSn) return true;
        if (first === toPart && last === fromPart && hasSn) return false;
        return null;
    }

    private _parseTripEndpoints(
        tripDescription: string,
        stops: LineViewStop[],
    ): { from: string; to: string; goingDown: boolean } | null {
        let idx = 0;
        while ((idx = tripDescription.indexOf('-', idx)) !== -1) {
            const from = tripDescription.substring(0, idx).trim();
            const to = tripDescription.substring(idx + 1).trim();
            const fromIdx = stops.findIndex(
                (s) => s.name.toLowerCase() === from.toLowerCase(),
            );
            const toIdx = stops.findIndex(
                (s) => s.name.toLowerCase() === to.toLowerCase(),
            );
            if (fromIdx >= 0 && toIdx >= 0 && fromIdx !== toIdx) {
                return { from, to, goingDown: toIdx > fromIdx };
            }
            idx++;
        }
        return null;
    }

    private _findCurrentAndNextActivity(
        vehicle: Vehicle,
        activities: VehicleBlockActivityInfo[],
    ): {
        currentActivity?: VehicleBlockActivityInfo;
        nextActivity?: VehicleBlockActivityInfo;
    } {
        if (!activities?.length) return {};

        // Use String() on both sides to avoid type-mismatch false negatives.
        const vTrip =
            vehicle.tripNumber == null ? undefined : String(vehicle.tripNumber);
        const vRoute =
            vehicle.routeNumber == null
                ? undefined
                : String(vehicle.routeNumber);
        const exactIdx =
            vTrip && vRoute
                ? activities.findIndex(
                      (a) =>
                          String(a.tripNumber) === vTrip &&
                          String(a.routeName) === vRoute,
                  )
                : -1;
        if (exactIdx >= 0) {
            return {
                currentActivity: activities[exactIdx],
                nextActivity: activities[exactIdx + 1],
            };
        }

        const now = new Date();
        const inFrameIdx = activities.findIndex(
            (a) =>
                a.startDateTime &&
                a.endDateTime &&
                now >= a.startDateTime &&
                now <= a.endDateTime,
        );
        if (inFrameIdx >= 0) {
            return {
                currentActivity: activities[inFrameIdx],
                nextActivity: activities[inFrameIdx + 1],
            };
        }

        const upcomingIdx = activities.findIndex(
            (a) => a.startDateTime && now < a.startDateTime,
        );
        if (upcomingIdx >= 0) {
            return { currentActivity: activities[upcomingIdx] };
        }

        return {};
    }
}
